const EMPTY_QUERY = {combinator: "and", rules: []};

// Mirrors the product search-document contract in DataSetDefinitionCompiler.
const PRODUCT_FIELD_ALIASES = {
    category: "category",
    categoryuuid: "categoryUuids",
    producttemplate: "productTemplate",
    updatedby: "updatedBy",
    totalquantityonhand: "totalQuantityOnHand",
    unitprice: "unitPrice",
    lastmodifieddate: "lastModifiedDate",
    fullvehicledescription: "fullVehicleDescription",
    vehicleyear: "vehicleYear",
    odometer: "odometer",
    warrantykms: "warrantyKMS",
    warrantymonths: "warrantyMonths",
    labels: "labels",
    inventorystorename: "inventory.storeName",
    inventorystorecode: "inventory.storeCode",
    inventorylocationcode: "inventory.locationCode",
    inventoryserialnumber: "inventory.serialNumber",
    inventorybatchnumber: "inventory.batchNumber",
    inventorystocknumber: "inventory.stockNumber",
    saleseventtitle: "salesEvent.title",
    saleseventeventtype: "salesEvent.eventType",
    saleseventstatus: "salesEvent.lotStatus",
    saleseventsellingmethod: "salesEvent.sellingMethod",
    saleseventreserve: "salesEvent.reserve",
    saleseventhighestbid: "salesEvent.highestBid",
    saleseventtotalbids: "salesEvent.totalBids",
    saleseventstartdate: "salesEvent.startDate",
    saleseventenddate: "salesEvent.endDate",
    saleseventwinner: "salesEvent.winner"
};

const PRODUCT_TEXT_KEYWORD_FIELDS = new Set([
    "category",
    "categoryUuids",
    "externalCatalogIdentifier",
    "display",
    "labels",
    "productTemplate",
    "title",
    "updatedBy",
    "sellingPriceDisclaimer",
    "warrantyKMS",
    "warrantyMonths",
    "inventory.storeName",
    "inventory.storeCode",
    "inventory.locationCode",
    "inventory.serialNumber",
    "inventory.batchNumber",
    "inventory.stockNumber"
]);
const PRODUCT_PRIMARY_KEYWORD_FIELDS = new Set([
    "salesEvent.title",
    "salesEvent.lotStatus",
    "salesEvent.sellingMethod",
    "salesEvent.winner"
]);
const UNMAPPED_PRODUCT_FIELDS = new Set(["salesEvent.eventType"]);
const PRODUCT_PRIMARY_DOC_VALUE_FIELDS = new Set([
    "imageCount",
    "totalQuantityOnHand",
    "costExTax",
    "discountedSellingPrice",
    "sellingPrice",
    "unitPrice",
    "vehicleYear",
    "odometer",
    "createdAt",
    "updatedAt",
    "lastModifiedDate",
    "salesEvent.reserve",
    "salesEvent.highestBid",
    "salesEvent.totalBids",
    "salesEvent.startDate",
    "salesEvent.endDate",
    ...PRODUCT_PRIMARY_KEYWORD_FIELDS
]);
const UNSORTABLE_PRODUCT_FIELD_IDS = new Set(["thumbnail", "description", "fullvehicledescription"]);

const DEFAULT_ADDRESS_FIELDS = ["addressLine1", "addressLine2", "suburb", "state", "postcode", "country"];
const FIELD_COMPARISON_OPERATORS = new Set(["equals", "notEquals", "gt", "gte", "lt", "lte"]);
const SUPPORTED_OPERATORS = {
    text: new Set(["contains", "notContains", "startsWith", "endsWith", "equals", "notEquals", "isEmpty", "isNotEmpty"]),
    number: new Set(["equals", "notEquals", "gt", "gte", "lt", "lte", "between", "isEmpty", "isNotEmpty"]),
    date: new Set(["equals", "notEquals", "gt", "gte", "lt", "lte", "between", "isEmpty", "isNotEmpty"]),
    boolean: new Set(["equals", "notEquals", "isEmpty", "isNotEmpty"]),
    select: new Set(["equals", "notEquals", "in", "notIn", "isEmpty", "isNotEmpty"])
};

function parseRecommendedDefinition(value) {
    if (!value) return {};

    if (typeof value === "object") {
        if (Array.isArray(value)) return {sort: value};
        if (Object.prototype.hasOwnProperty.call(value, "sort") || value.query || value.queryBuilder || value.sortBuilder) return value;
        return {sort: [value]};
    }

    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return {sort: parsed};
        if (parsed && typeof parsed === "object") {
            if (Object.prototype.hasOwnProperty.call(parsed, "sort") || parsed.query || parsed.queryBuilder || parsed.sortBuilder) return parsed;
            return {sort: [parsed]};
        }
    } catch (err) {
        console.error("Failed to parse recommended definition", err);
    }

    return {};
}

function normalizeRecommendedQuery(query) {
    if (!query || typeof query !== "object" || !Array.isArray(query.rules)) return EMPTY_QUERY;

    return {
        combinator: query.combinator === "or" ? "or" : "and",
        rules: query.rules
    };
}

function normalizePriorityValues(values) {
    const rawValues = Array.isArray(values) ? values : [values];
    return rawValues
        .flatMap(value => ["string", "number", "boolean"].includes(typeof value) ? `${value}`.split("\n") : [])
        .map(value => value.trim())
        .filter(Boolean)
        .filter((value, index, all) => all.indexOf(value) === index);
}

function normalizeDefaultRank(defaultRank) {
    if (defaultRank == null || `${defaultRank}`.trim() === "") return null;
    const rank = Number(defaultRank);
    return Number.isSafeInteger(rank) && rank >= 1 ? rank : null;
}

function normalizeFieldPriority(priority) {
    const field = typeof priority?.field === "string" ? priority.field.trim() : "";
    const values = normalizePriorityValues(priority?.values).join("\n");
    const defaultRank = normalizeDefaultRank(priority?.defaultRank);
    return {
        field,
        values,
        ...(defaultRank != null ? {defaultRank} : {})
    };
}

function normalizeRecommendedSortRule(rule) {
    if (rule?.type === "fieldPriority") {
        return {
            type: "combinedFieldPriority",
            priorities: [normalizeFieldPriority(rule)]
        };
    }

    if (rule?.type === "combinedFieldPriority") {
        return {
            ...rule,
            priorities: Array.isArray(rule.priorities) ? rule.priorities.map(normalizeFieldPriority) : []
        };
    }

    return rule && typeof rule === "object" ? rule : null;
}

function normalizeRecommendedSortRules(sortBuilder) {
    return Array.isArray(sortBuilder) ? sortBuilder.map(normalizeRecommendedSortRule).filter(Boolean) : [];
}

function resolveProductFieldName(fieldName) {
    if (!fieldName || typeof fieldName !== "string") return fieldName;

    const alias = PRODUCT_FIELD_ALIASES[fieldName.toLowerCase()];
    if (alias) return alias;

    return fieldName[0] === fieldName[0].toUpperCase()
        ? `${fieldName[0].toLowerCase()}${fieldName.slice(1)}`
        : fieldName;
}

function getFieldDataType(field) {
    if (SUPPORTED_OPERATORS[field?.dataType]) return field.dataType;
    if (["checkbox", "switch"].includes(field?.type)) return "boolean";
    if (["select", "combo-box", "radio", "checkbox-list"].includes(field?.type)) return "select";
    if (["datetime-local", "date-of-birth"].includes(field?.type)) return "date";
    if (["number", "currency"].includes(field?.type)) return "number";
    return "text";
}

function getLookupField(fieldLookup, fieldName) {
    if (!fieldName || typeof fieldName !== "string") return null;
    return fieldLookup[fieldName] ?? fieldLookup[fieldName.toLowerCase()] ?? null;
}

function getConfiguredOperators(field) {
    const configuredOperators = Array.isArray(field?.operators)
        ? field.operators.map(operator => typeof operator === "string" ? operator : operator?.name).filter(Boolean)
        : [];

    return configuredOperators.length > 0
        ? new Set(configuredOperators)
        : SUPPORTED_OPERATORS[getFieldDataType(field)];
}

function getRuleValue(value) {
    if (value && typeof value === "object" && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, "value")) {
        return value.value;
    }

    return value;
}

function getArrayValues(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") return value.split(",").map(item => item.trim()).filter(Boolean);
    return value == null ? [] : [value];
}

function isBlankValue(value) {
    return value == null || (typeof value === "string" && value.trim() === "");
}

function isUuidValue(value) {
    return typeof value === "string" && /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(value);
}

function shouldFilterCategoryByUuid(field, rawValue) {
    if (`${field?.name ?? ""}`.toLowerCase() !== "category") return false;
    const values = getArrayValues(getRuleValue(rawValue)).filter(value => !isBlankValue(value));
    return values.length > 0 && values.every(isUuidValue);
}

function resolveProductFieldPaths(field, rawValue) {
    if (shouldFilterCategoryByUuid(field, rawValue)) {
        return {
            primaryField: "categoryUuids",
            exactFields: ["categoryUuids.keyword"],
            filterFields: ["categoryUuids.keyword"]
        };
    }

    const primaryField = resolveProductFieldName(field.name);
    if (field.type === "address") {
        const addressFields = Array.isArray(field.fields) && field.fields.length > 0 ? field.fields : DEFAULT_ADDRESS_FIELDS;
        const exactFields = addressFields.map(addressField => `${primaryField}.${addressField}.keyword`);
        return {primaryField, exactFields, filterFields: exactFields};
    }

    const dataType = getFieldDataType(field);
    const exactField = PRODUCT_PRIMARY_KEYWORD_FIELDS.has(primaryField)
        ? primaryField
        : dataType === "select" || (dataType === "text" && PRODUCT_TEXT_KEYWORD_FIELDS.has(primaryField))
            ? keywordFieldCandidate(primaryField)
            : null;

    return {
        primaryField,
        exactFields: exactField ? [exactField] : [],
        filterFields: [exactField ?? primaryField]
    };
}

function createFieldShouldClause(fields, clauseFactory) {
    const clauses = fields.map(clauseFactory);
    return clauses.length === 1
        ? clauses[0]
        : {bool: {should: clauses, minimum_should_match: 1}};
}

function escapeWildcard(value) {
    return `${value}`.replace(/\\/g, "\\\\").replace(/\*/g, "\\*").replace(/\?/g, "\\?");
}

function createWildcardMatch(fields, pattern) {
    return createFieldShouldClause(fields, fieldName => ({
        wildcard: {
            [fieldName]: {
                value: pattern,
                case_insensitive: true
            }
        }
    }));
}

function wrapMustNot(clause) {
    return {bool: {must_not: [clause]}};
}

function normalizeBooleanValue(value) {
    if (value === true || value === "true") return true;
    if (value === false || value === "false") return false;
    return null;
}

function normalizeExactValue(value, dataType) {
    return dataType === "boolean" ? normalizeBooleanValue(value) : value;
}

function createExactMatch(paths, field, rawValue) {
    const dataType = getFieldDataType(field);
    const value = normalizeExactValue(rawValue, dataType);
    if (value == null) return null;

    if (dataType === "text") {
        const fields = paths.exactFields.length > 0 ? paths.exactFields : paths.filterFields;
        return createFieldShouldClause(fields, fieldName => paths.exactFields.length > 0
            ? {term: {[fieldName]: `${value}`}}
            : {match_phrase: {[fieldName]: `${value}`}});
    }

    return {term: {[paths.exactFields[0] ?? paths.primaryField]: value}};
}

function createInMatch(paths, field, rawValue) {
    const dataType = getFieldDataType(field);
    const values = getArrayValues(rawValue)
        .filter(value => !isBlankValue(value))
        .map(value => normalizeExactValue(value, dataType))
        .filter(value => value != null);
    if (values.length === 0) return null;

    if (dataType === "text") {
        const fields = paths.exactFields.length > 0 ? paths.exactFields : paths.filterFields;
        return createFieldShouldClause(fields, fieldName => paths.exactFields.length > 0
            ? {terms: {[fieldName]: values}}
            : {bool: {should: values.map(value => ({match_phrase: {[fieldName]: value}})), minimum_should_match: 1}});
    }

    return {terms: {[paths.exactFields[0] ?? paths.primaryField]: values}};
}

function isTextLikeField(field) {
    return !["number", "date", "boolean"].includes(getFieldDataType(field));
}

function createEmptyClause(paths, field) {
    return isTextLikeField(field)
        ? wrapMustNot(createWildcardMatch(paths.filterFields, "?*"))
        : wrapMustNot({exists: {field: paths.primaryField}});
}

function createNotEmptyClause(paths, field) {
    return isTextLikeField(field)
        ? createWildcardMatch(paths.filterFields, "?*")
        : {exists: {field: paths.primaryField}};
}

function createRange(fieldName, operator, value) {
    return {range: {[fieldName]: {[operator]: value}}};
}

function createBetweenRange(fieldName, values) {
    return {range: {[fieldName]: {gte: values[0], lte: values[1]}}};
}

function createDateMathRange(fieldName, gte, lt) {
    return {range: {[fieldName]: {gte, lt}}};
}

function addOneDay(dateValue) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
    if (!match) return null;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    if (
        Number.isNaN(date.getTime()) ||
        date.getUTCFullYear() !== Number(match[1]) ||
        date.getUTCMonth() !== Number(match[2]) - 1 ||
        date.getUTCDate() !== Number(match[3])
    ) return null;
    date.setUTCDate(date.getUTCDate() + 1);
    return date.toISOString().slice(0, 10);
}

function isValidDateBoundary(value) {
    if (value === "now") return true;
    if (typeof value !== "string") return false;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return addOneDay(value) != null;

    const dateTimeMatch = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?$/.exec(value);
    if (!dateTimeMatch || addOneDay(dateTimeMatch[1]) == null) return false;
    const hour = Number(dateTimeMatch[2]);
    const minute = Number(dateTimeMatch[3]);
    const second = Number(dateTimeMatch[4] ?? 0);
    return hour <= 23 && minute <= 59 && second <= 59;
}

function isValidNumberValue(value) {
    return typeof value === "number"
        ? Number.isFinite(value)
        : typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value));
}

function createRelativePresetRange(fieldName, preset) {
    const ranges = {
        today: ["now/d", "now+1d/d"],
        yesterday: ["now-1d/d", "now/d"],
        tomorrow: ["now+1d/d", "now+2d/d"],
        thisweek: ["now/w", "now+1w/w"],
        lastweek: ["now-1w/w", "now/w"],
        nextweek: ["now+1w/w", "now+2w/w"],
        thismonth: ["now/M", "now+1M/M"],
        lastmonth: ["now-1M/M", "now/M"],
        nextmonth: ["now+1M/M", "now+2M/M"],
        thisyear: ["now/y", "now+1y/y"],
        lastyear: ["now-1y/y", "now/y"],
        nextyear: ["now+1y/y", "now+2y/y"]
    };
    const range = ranges[`${preset ?? ""}`.toLowerCase()];
    return range ? createDateMathRange(fieldName, range[0], range[1]) : null;
}

function createRollingRange(fieldName, value) {
    const amount = Number(value?.amount);
    const unit = {
        hour: "h",
        hours: "h",
        day: "d",
        days: "d",
        week: "w",
        weeks: "w",
        month: "M",
        months: "M",
        year: "y",
        years: "y"
    }[`${value?.unit ?? ""}`.toLowerCase()];
    if (!Number.isInteger(amount) || amount <= 0 || !unit) return null;

    return `${value?.direction ?? ""}`.toLowerCase() === "future"
        ? createDateMathRange(fieldName, "now", `now+${amount}${unit}`)
        : createDateMathRange(fieldName, `now-${amount}${unit}`, "now");
}

function createDatePeriodRange(fieldName, rawValue) {
    if (rawValue && typeof rawValue === "object" && !Array.isArray(rawValue)) {
        if (rawValue.mode === "preset") return createRelativePresetRange(fieldName, rawValue.preset);
        if (rawValue.mode === "rolling") return createRollingRange(fieldName, rawValue);
        if (rawValue.mode === "specific") return createDatePeriodRange(fieldName, rawValue.value);
        return null;
    }

    if (typeof rawValue !== "string") return null;
    const end = addOneDay(rawValue);
    return end ? createDateMathRange(fieldName, rawValue, end) : null;
}

function getDateBoundary(rawValue) {
    if (rawValue && typeof rawValue === "object" && !Array.isArray(rawValue)) {
        if (rawValue.mode === "now") return "now";
        if (rawValue.mode === "specific" && !isBlankValue(rawValue.value)) return rawValue.value;
        return null;
    }

    return isBlankValue(rawValue) ? null : rawValue;
}

function compileDateRule(operator, paths, rawValue) {
    const fieldName = paths.primaryField;
    if (["equals", "notEquals"].includes(operator)) {
        const normalizedValue = getRuleValue(rawValue);
        const periodRange = createDatePeriodRange(fieldName, rawValue);
        const clause = periodRange ?? (isValidDateBoundary(normalizedValue) ? {term: {[fieldName]: normalizedValue}} : null);
        if (!clause) return null;
        return operator === "notEquals" ? wrapMustNot(clause) : clause;
    }

    if (operator === "between") {
        const values = getArrayValues(rawValue).map(getDateBoundary);
        return values.length === 2 && values.every(isValidDateBoundary)
            ? createBetweenRange(fieldName, values)
            : null;
    }

    if (["gt", "lt"].includes(operator) && rawValue?.mode === "rolling") {
        return createRollingRange(fieldName, rawValue);
    }

    const boundary = getDateBoundary(rawValue);
    return isValidDateBoundary(boundary) ? createRange(fieldName, operator, boundary) : null;
}

function createFieldValueExpression(field, fieldName) {
    return getFieldDataType(field) === "date"
        ? `doc['${fieldName}'].value.toInstant().toEpochMilli()`
        : `doc['${fieldName}'].value`;
}

function createFieldComparison(field, referencedField, operator) {
    const left = resolveProductFieldPaths(field).primaryField;
    const right = resolveProductFieldPaths(referencedField).primaryField;
    const safeLeft = escapePainlessString(left);
    const safeRight = escapePainlessString(right);
    const comparisonOperator = {
        equals: "==",
        notEquals: "!=",
        gt: ">",
        gte: ">=",
        lt: "<",
        lte: "<="
    }[operator];
    const leftExpression = createFieldValueExpression(field, safeLeft);
    const rightExpression = createFieldValueExpression(field, safeRight);

    return {
        script: {
            script: {
                lang: "painless",
                source: `doc['${safeLeft}'].size()!=0 && doc['${safeRight}'].size()!=0 && (${leftExpression} ${comparisonOperator} ${rightExpression})`
            }
        }
    };
}

function compileRecommendedRule(rule, fieldLookup) {
    const field = getLookupField(fieldLookup, rule?.field);
    if (!field || !rule?.operator || !getConfiguredOperators(field).has(rule.operator)) return null;
    if (UNMAPPED_PRODUCT_FIELDS.has(resolveProductFieldName(field.name))) return null;

    const dataType = getFieldDataType(field);
    const referencedFieldName = rule.value?.mode === "field" ? rule.value.field : null;
    if (referencedFieldName) {
        const referencedField = getLookupField(fieldLookup, referencedFieldName);
        if (!referencedField || !FIELD_COMPARISON_OPERATORS.has(rule.operator)) return null;
        if (!["number", "date"].includes(dataType) || getFieldDataType(referencedField) !== dataType) return null;
        return createFieldComparison(field, referencedField, rule.operator);
    }
    if (rule.value?.mode === "field") return null;

    const paths = resolveProductFieldPaths(field, rule.value);
    if (rule.operator === "isEmpty") return createEmptyClause(paths, field);
    if (rule.operator === "isNotEmpty") return createNotEmptyClause(paths, field);

    const value = getRuleValue(rule.value);
    if (isBlankValue(value) || (Array.isArray(value) && value.every(isBlankValue))) return null;

    if (dataType === "date") return compileDateRule(rule.operator, paths, rule.value);
    if (dataType === "number" && rule.operator !== "between" && !isValidNumberValue(value)) return null;
    if (rule.operator === "between") {
        const values = getArrayValues(value);
        return values.length === 2 && values.every(item => dataType === "number" ? isValidNumberValue(item) : !isBlankValue(item))
            ? createBetweenRange(paths.primaryField, values)
            : null;
    }

    if (value && typeof value === "object" && !Array.isArray(value)) return null;

    switch (rule.operator) {
        case "equals":
            return Array.isArray(value) ? createInMatch(paths, field, value) : createExactMatch(paths, field, value);
        case "notEquals": {
            const clause = Array.isArray(value) ? createInMatch(paths, field, value) : createExactMatch(paths, field, value);
            return clause ? wrapMustNot(clause) : null;
        }
        case "in":
            return createInMatch(paths, field, value);
        case "notIn": {
            const clause = createInMatch(paths, field, value);
            return clause ? wrapMustNot(clause) : null;
        }
        case "contains":
            return createWildcardMatch(paths.filterFields, `*${escapeWildcard(value)}*`);
        case "notContains":
            return wrapMustNot(createWildcardMatch(paths.filterFields, `*${escapeWildcard(value)}*`));
        case "startsWith":
            return createWildcardMatch(paths.filterFields, `${escapeWildcard(value)}*`);
        case "endsWith":
            return createWildcardMatch(paths.filterFields, `*${escapeWildcard(value)}`);
        case "gt":
        case "gte":
        case "lt":
        case "lte":
            return createRange(paths.primaryField, rule.operator, value);
        default:
            return null;
    }
}

function compileRecommendedQueryNode(node, fieldLookup) {
    if (!node || typeof node !== "object") return null;

    if (Array.isArray(node.rules)) {
        const clauses = node.rules.map(rule => compileRecommendedQueryNode(rule, fieldLookup)).filter(Boolean);
        if (clauses.length === 0) return null;

        return node.combinator === "or"
            ? {bool: {should: clauses, minimum_should_match: 1}}
            : {bool: {must: clauses}};
    }

    return compileRecommendedRule(node, fieldLookup);
}

function sanitizeRecommendedQueryNode(node, fieldLookup) {
    if (!node || typeof node !== "object") return null;

    if (Array.isArray(node.rules)) {
        const rules = node.rules.map(rule => sanitizeRecommendedQueryNode(rule, fieldLookup)).filter(Boolean);
        if (rules.length === 0) return null;
        return {
            ...(node.id != null ? {id: node.id} : {}),
            combinator: node.combinator === "or" ? "or" : "and",
            rules
        };
    }

    if (!compileRecommendedRule(node, fieldLookup)) return null;
    return {
        ...(node.id != null ? {id: node.id} : {}),
        field: node.field,
        operator: node.operator,
        ...(!["isEmpty", "isNotEmpty"].includes(node.operator) ? {value: node.value} : {})
    };
}

function createFieldLookup(queryBuilderFields) {
    return (queryBuilderFields ?? []).reduce((lookup, field) => {
        if (!field?.name) return lookup;
        lookup[field.name] = field;
        lookup[field.name.toLowerCase()] = field;
        return lookup;
    }, {});
}

function buildQueryDefinition(query, queryBuilderFields) {
    const fieldLookup = createFieldLookup(queryBuilderFields);
    const normalizedQuery = normalizeRecommendedQuery(query);
    const queryBuilder = sanitizeRecommendedQueryNode(normalizedQuery, fieldLookup) ?? EMPTY_QUERY;
    const compiledQuery = compileRecommendedQueryNode(queryBuilder, fieldLookup);

    return {
        version: 1,
        queryBuilder,
        ...(compiledQuery ? {query: compiledQuery} : {})
    };
}

function createPresenceSort(fieldName) {
    const resolvedFieldName = resolveProductFieldName(fieldName);
    const safeFieldName = escapePainlessString(resolvedFieldName);

    return {
        _script: {
            type: "number",
            script: {
                lang: "painless",
                source: `if(doc.containsKey('${safeFieldName}') && doc['${safeFieldName}'].size() != 0 && doc['${safeFieldName}'].value > 0) { return 0;} return 1;`
            },
            order: "asc"
        }
    };
}

function escapePainlessString(value) {
    return `${value}`.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}

function isPriorityFieldSortable(field, fieldName) {
    if (UNSORTABLE_PRODUCT_FIELD_IDS.has(`${fieldName ?? ""}`.toLowerCase())) return false;
    if (!field) return true;
    if (field.isSortable === false) return false;

    const fieldType = `${field.type ?? ""}`.toLowerCase();
    return fieldType !== "textarea" &&
        !fieldType.includes("rich-text") &&
        !fieldType.includes("richtext") &&
        !fieldType.includes("upload") &&
        !fieldType.includes("image");
}

function keywordFieldCandidate(primaryField) {
    return primaryField.endsWith(".keyword") ? primaryField : `${primaryField}.keyword`;
}

function resolvePrioritySortFields(fieldName, fieldLookup, hasFieldMetadata) {
    if (typeof fieldName !== "string" || !fieldName.trim()) return null;
    const normalizedFieldName = fieldName.trim();
    const field = getLookupField(fieldLookup, normalizedFieldName);
    if (hasFieldMetadata && !field) return null;
    if (!isPriorityFieldSortable(field, normalizedFieldName)) return null;

    if (field) {
        const paths = resolveProductFieldPaths(field);
        const primaryField = paths.primaryField;
        if (UNMAPPED_PRODUCT_FIELDS.has(primaryField)) return null;
        const dataType = getFieldDataType(field);
        if (["number", "date", "boolean"].includes(dataType) || PRODUCT_PRIMARY_DOC_VALUE_FIELDS.has(primaryField)) {
            return [primaryField];
        }
        if (field.type === "address") return paths.exactFields.length > 0 ? [paths.exactFields[0]] : null;
        if (dataType === "select") return paths.exactFields.length > 0 ? [paths.exactFields[0]] : null;
        return [keywordFieldCandidate(primaryField)];
    }

    const resolvedFieldName = resolveProductFieldName(normalizedFieldName);
    if (!resolvedFieldName) return null;
    if (UNMAPPED_PRODUCT_FIELDS.has(resolvedFieldName)) return null;
    if (resolvedFieldName.endsWith(".keyword") || PRODUCT_PRIMARY_DOC_VALUE_FIELDS.has(resolvedFieldName)) {
        return [resolvedFieldName];
    }
    return [keywordFieldCandidate(resolvedFieldName)];
}

function getSafeScriptName(field, index) {
    const name = `${field || "field"}`
        .replace(/\.keyword$/, "")
        .replace(/[^A-Za-z0-9_]/g, "");

    const safeName = !name
        ? "field"
        : /^[A-Za-z_]/.test(name) ? name : `field${name}`;
    return `${safeName}${index + 1}`;
}

function createPriorityRankDeclaration(priority) {
    const mapEntries = priority.values.map((value, index) => `'${escapePainlessString(value)}':${index + 1}`).join(", ");
    const fallback = priority.defaultRank ?? priority.values.length + 1;
    const prioritiesName = `${priority.scriptName}Priorities`;
    const rankName = `${priority.scriptName}Rank`;
    const valueChecks = priority.fields.map((fieldName, fieldIndex) => {
        const safeFieldName = escapePainlessString(fieldName);
        const valueName = `${priority.scriptName}Value${fieldIndex + 1}`;
        const indexName = `${valueName}Index`;
        const candidateRankName = `${valueName}Rank`;

        return `if (doc.containsKey('${safeFieldName}') && doc['${safeFieldName}'].size() != 0) {
    for (int ${indexName} = 0; ${indexName} < doc['${safeFieldName}'].size(); ${indexName}++) {
        String ${valueName} = doc['${safeFieldName}'][${indexName}].toString();
        def ${candidateRankName} = ${prioritiesName}[${valueName}];
        if (${candidateRankName} != null && ${candidateRankName} < ${rankName}) { ${rankName} = ${candidateRankName}; }
    }
}`;
    });

    return {
        source: `Map ${prioritiesName} = [${mapEntries}];\nlong ${rankName} = ${fallback}L;\n${valueChecks.join("\n")}`,
        rankName
    };
}

function createCombinedFieldPrioritySort(rule, fieldLookup, hasFieldMetadata) {
    const priorities = (rule.priorities ?? [])
        .map((priority, index) => ({
            fields: resolvePrioritySortFields(priority.field, fieldLookup, hasFieldMetadata),
            values: normalizePriorityValues(priority.values),
            defaultRank: normalizeDefaultRank(priority.defaultRank),
            scriptName: getSafeScriptName(priority.field, index)
        }))
        .filter(priority => priority.fields?.length > 0 && priority.values.length > 0);

    if (priorities.length === 0) return null;

    const declarations = priorities.map(createPriorityRankDeclaration);
    const returnExpression = declarations.map(declaration => declaration.rankName).join(" + ");

    return {
        _script: {
            type: "number",
            script: {
                lang: "painless",
                source: `${declarations.map(declaration => declaration.source).join("\n")}\nreturn ${returnExpression}`
            },
            order: "asc"
        }
    };
}

function createFieldPrioritySort(rule, fieldLookup, hasFieldMetadata) {
    return createCombinedFieldPrioritySort({priorities: [normalizeFieldPriority(rule)]}, fieldLookup, hasFieldMetadata);
}

function compileRecommendedSortRules(sortRules, queryBuilderFields) {
    const fieldLookup = createFieldLookup(queryBuilderFields);
    const hasFieldMetadata = Array.isArray(queryBuilderFields) && queryBuilderFields.length > 0;
    return normalizeRecommendedSortRules(sortRules)
        .map(rule => {
            if (rule?.type === "hasImagesFirst") return createPresenceSort("imageCount");
            if (rule?.type === "hasPriceFirst") return createPresenceSort("sellingPrice");
            if (rule?.type === "combinedFieldPriority") return createCombinedFieldPrioritySort(rule, fieldLookup, hasFieldMetadata);
            if (rule?.type === "fieldPriority") return createFieldPrioritySort(rule, fieldLookup, hasFieldMetadata);
            return null;
        })
        .filter(Boolean);
}

function buildRecommendedDefinition(query, sortRules, queryBuilderFields) {
    const normalizedQuery = normalizeRecommendedQuery(query);
    const normalizedSortRules = normalizeRecommendedSortRules(sortRules);
    const queryDefinition = buildQueryDefinition(normalizedQuery, queryBuilderFields);
    const sort = compileRecommendedSortRules(normalizedSortRules, queryBuilderFields);

    return {
        version: 1,
        queryBuilder: queryDefinition.queryBuilder,
        sortBuilder: normalizedSortRules,
        ...(queryDefinition.query ? {query: queryDefinition.query} : {}),
        ...(sort.length ? {sort} : {})
    };
}

function buildRecommendedSortDefinition(sortRules, queryBuilderFields) {
    const normalizedSortRules = normalizeRecommendedSortRules(sortRules);
    const sort = compileRecommendedSortRules(normalizedSortRules, queryBuilderFields);

    return {
        version: 1,
        sortBuilder: normalizedSortRules,
        ...(sort.length ? {sort} : {})
    };
}

module.exports = {
    EMPTY_QUERY,
    buildQueryDefinition,
    buildRecommendedDefinition,
    buildRecommendedSortDefinition,
    compileRecommendedSortRules,
    compileRecommendedQueryNode,
    normalizeRecommendedQuery,
    normalizeRecommendedSortRules,
    parseRecommendedDefinition,
    resolveProductFieldName
};
